// src/stores/CalculationStore.ts
import { makeAutoObservable } from 'mobx';
import {
  CreatorRole,
  ProcessListForChangeDto,
} from '@codefluegel/zeta-change-typescript-client';
import { getSumOfWP } from '../utils/getTotalTime';
import { getDepartmentById } from '../utils/utils';
import { IChangeStore } from './changeStore';

class CalculationStore {
  // Reference to the main store. All calculations will use this.
  private changeStore: IChangeStore;

  constructor(changeStore: IChangeStore) {
    this.changeStore = changeStore;
    makeAutoObservable(this);
  }

  // Corresponds to the useEffect/useState for 'evaluatedHoursCosts'
  get evaluatedHoursCosts(): number {
    const { change, reducedWorkPackages } = this.changeStore;
    if (!change?.processes) {
      return 0;
    }

    const totalSum = change.processes.reduce(
      (acc: number, process: ProcessListForChangeDto) => {
        const department = getDepartmentById(
          process.pId,
          change.commonProject?.navProcessToCommonProjectRelations,
        );
        const hourlyRate = process.finalHourlyRate
          ? process.finalHourlyRate
          : department?.hourlyRate;

        if (!reducedWorkPackages[process.id] || !hourlyRate) return acc;

        return (
          acc + (getSumOfWP(reducedWorkPackages[process.id], hourlyRate) || 0)
        );
      },
      0,
    );

    return totalSum;
  }

  // Corresponds to the 'implementedCount' useMemo
  get implementedCount(): number {
    return (
      this.changeStore.workPackages?.filter(wp => wp.status === 'IMPLEMENTED')
        .length || 0
    );
  }

  // Corresponds to the 'workPackagesCount' useMemo
  get workPackagesCount(): number {
    return this.changeStore.workPackages?.length || 0;
  }

  // Corresponds to the 'calculatedHoursCosts' useMemo
  // This now calls the method on the main store.
  get calculatedHoursCosts(): number {
    return this.changeStore.getCalculatedHoursCosts(false);
  }

  // Corresponds to the 'releasedHoursCosts' useMemo
  get releasedHoursCosts(): number {
    return this.changeStore.getCalculatedHoursCosts(true);
  }

  // Corresponds to the 'calculatedHours' useMemo
  get calculatedHours(): number {
    return this.changeStore.getCalculatedHours(false);
  }

  // Corresponds to the 'releasedHours' useMemo
  get releasedHours(): number {
    return this.changeStore.getCalculatedHours(true);
  }

  // Corresponds to the 'evalutaedMaterialCosts' useMemo
  get evalutaedMaterialCosts(): number {
    return this.changeStore.getTotalWorkMaterialHoursOfUsedProcesses(
      CreatorRole.CoreTeamMember,
    );
  }

  // Corresponds to the 'calculatedMaterialCosts' useMemo
  get calculatedMaterialCosts(): number {
    return this.changeStore.getTotalWorkMaterialHoursOfUsedProcesses(
      CreatorRole.CalculationResponsible,
    );
  }

  // Corresponds to the 'releasedMaterialCosts' useMemo
  get releasedMaterialCosts(): number {
    return this.changeStore.getTotalWorkMaterialHoursOfUsedProcesses(
      CreatorRole.ChangeManager,
    );
  }

  // Corresponds to the 'evalutaedServiceCosts' useMemo
  get evalutaedServiceCosts(): number {
    return this.changeStore.getTotalWorkServicesHoursOfUsedProcesses(
      CreatorRole.CoreTeamMember,
    );
  }

  // Corresponds to the 'calculatedServiceCosts' useMemo
  get calculatedServiceCosts(): number {
    return this.changeStore.getTotalWorkServicesHoursOfUsedProcesses(
      CreatorRole.CalculationResponsible,
    );
  }

  // Corresponds to the 'releasedServiceCosts' useMemo
  get releasedServiceCosts(): number {
    return this.changeStore.getTotalWorkServicesHoursOfUsedProcesses(
      CreatorRole.ChangeManager,
    );
  }

  // Corresponds to the 'hoursValues' useMemo
  get hoursValues(): number[] {
    return [
      this.changeStore.totalWorkPackageHoursOfUsedProcesses,
      this.calculatedHours,
      this.releasedHours,
    ];
  }

  // Corresponds to the 'hoursCosts' useMemo
  get hoursCosts(): number[] {
    return [
      this.evaluatedHoursCosts,
      this.calculatedHoursCosts,
      this.releasedHoursCosts,
    ];
  }

  // Corresponds to the 'materialValues' useMemo
  get materialValues(): number[] {
    return [
      this.evalutaedMaterialCosts + this.evalutaedServiceCosts,
      this.calculatedMaterialCosts + this.calculatedServiceCosts,
      this.releasedMaterialCosts + this.releasedServiceCosts,
    ];
  }

  // Corresponds to the 'totalSumValues' useMemo
  get totalSumValues(): number[] {
    return [
      this.evaluatedHoursCosts +
        this.evalutaedMaterialCosts +
        this.evalutaedServiceCosts,
      (this.calculatedHoursCosts ?? 0) +
        this.calculatedMaterialCosts +
        this.calculatedServiceCosts,
      (this.releasedHoursCosts ?? 0) +
        this.releasedMaterialCosts +
        this.releasedServiceCosts,
    ];
  }
}

export default CalculationStore;
